<template>
    <footer class="l2-landing-footer" aria-label="Footer">
        <div class="l2-landing-footer__container">
            <div class="l2-landing-footer__brand">
                <img class="l2-landing-footer__logo" :src="logoSrc" alt="Mix n' Batch" />
                <span class="l2-landing-footer__name">Mix n' Batch</span>
            </div>

            <div class="l2-landing-footer__links">
                <a class="l2-landing-footer__link" href="/blog">Blog</a>
                <span class="l2-landing-footer__dot">•</span>
                <a class="l2-landing-footer__link" href="#terms" @click.prevent="openTerms">Terms</a>
                <span class="l2-landing-footer__dot">•</span>
                <a class="l2-landing-footer__link" href="#privacy" @click.prevent="openPrivacy">Privacy</a>
            </div>

            <small class="l2-landing-footer__copyright">&copy; {{ currentYear }} Mix n' Batch. All rights reserved.</small>
        </div>

        <TermsOfServiceDialog v-model="termsOpen" :theme="vuetifyTheme" />
        <PrivacyPolicyDialog v-model="privacyOpen" :theme="vuetifyTheme" />
    </footer>
</template>

<script lang="ts" setup>
    import PrivacyPolicyDialog from '@/components/global/legal/PrivacyPolicyDialog.vue';
    import TermsOfServiceDialog from '@/components/global/legal/TermsOfServiceDialog.vue';
    import { useLegalPolicyDialogs } from '@/composables/useLegalPolicyDialogs';
    import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import { computed, watch } from 'vue';

    const props = defineProps<{
        theme: LandingTheme;
    }>();

    const emit = defineEmits<{
        (e: 'dialog-state', open: boolean): void;
    }>();

    const currentYear = computed(() => new Date().getFullYear());
    const vuetifyTheme = computed(() => (props.theme === 'light' ? 'naiveLight' : 'naiveDark'));
    const { termsOpen, privacyOpen, openTerms, openPrivacy } = useLegalPolicyDialogs();
    const logoSrc = '/logo/logo_purple.png';

    watch(
        [termsOpen, privacyOpen],
        () => {
            emit('dialog-state', termsOpen.value || privacyOpen.value);
        },
        { immediate: true, flush: 'sync' },
    );
</script>

<style lang="scss" scoped>
    .l2-landing-footer {
        position: relative;
        padding-bottom: 1em;
        background: var(--l2-bg);

        &__container {
            max-width: 1100px;
            margin: 0 auto;
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        &__brand {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
        }

        &__logo {
            height: 44px;
            width: auto;
        }

        &__name {
            font-weight: 700;
            color: var(--l2-text);
        }

        &__links {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            flex-wrap: wrap;
            color: var(--l2-text-muted);
        }

        &__link {
            color: inherit;
            text-decoration: none;

            &:hover {
                color: var(--l2-text);
                text-decoration: underline;
            }
        }

        &__dot {
            color: var(--l2-text-muted);
        }

        &__copyright {
            color: var(--l2-text-muted);
        }
    }
</style>
